import React from 'react';
import { FileText, Weight } from 'lucide-react';
import type { ProductFormData } from '@/modelos/productos/Producto';
import type { FormError } from '@/hooks/productos/useProductoFormulario';
import { Input } from '@/contasis';

interface ProductDescriptionSectionProps {
  formData: ProductFormData;
  setFormData: React.Dispatch<React.SetStateAction<ProductFormData>>;
  errors: FormError;
  isFieldVisible: (fieldId: string) => boolean;
  isFieldRequired: (fieldId: string) => boolean;
  isDescriptionExpanded: boolean;
  onToggleDescription: () => void;
  onBlur?: () => void;
  showCheck?: boolean;
  renderCheck?: (className?: string) => React.ReactNode;
}

export const ProductDescriptionField: React.FC<ProductDescriptionSectionProps> = ({
  formData,
  setFormData,
  errors,
  isFieldVisible,
  isFieldRequired,
  isDescriptionExpanded,
  onToggleDescription,
  onBlur,
  showCheck,
  renderCheck
}) => {
  return (
    <>
      {isFieldVisible('descripcion') && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="descripcion" className="text-xs font-medium text-gray-700">
              Descripción
              {isFieldRequired('descripcion') && <span className="text-red-500 ml-1">*</span>}
            </label>
            <button
              type="button"
              onClick={onToggleDescription}
              className="text-xs text-violet-600 hover:text-violet-700 font-medium"
            >
              {isDescriptionExpanded ? 'Contraer' : 'Expandir'}
            </button>
          </div>
          <div className="relative">
            <FileText className="absolute left-3 top-3 w-3.5 h-3.5 text-gray-400" />
            <textarea
              id="descripcion"
              rows={isDescriptionExpanded ? 6 : 3}
              value={formData.descripcion}
              onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
              onBlur={onBlur}
              className="w-full pl-9 pr-7 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500 resize-none"
              placeholder="Descripción detallada..."
            />
            {showCheck && renderCheck?.('absolute right-2 top-3')}
          </div>
          {errors.descripcion && <p className="text-red-600 text-xs mt-1">{errors.descripcion}</p>}
        </div>
      )}
    </>
  );
};

interface ProductWeightFieldProps {
  formData: ProductFormData;
  setFormData: React.Dispatch<React.SetStateAction<ProductFormData>>;
  errors: FormError;
  isFieldVisible: (fieldId: string) => boolean;
  isFieldRequired: (fieldId: string) => boolean;
  onBlur?: () => void;
  showCheck?: boolean;
  renderCheck?: (className?: string) => React.ReactNode;
}

export const ProductWeightField: React.FC<ProductWeightFieldProps> = ({
  formData,
  setFormData,
  errors,
  isFieldVisible,
  isFieldRequired,
  onBlur,
  showCheck,
  renderCheck
}) => {
  if (!isFieldVisible('peso')) return null;

  return (
    <Input
      id="peso"
      type="number"
      label="Peso (KGM)"
      required={isFieldRequired('peso')}
      step={0.001}
      min={0}
      value={formData.peso}
      onChange={(e) => setFormData(prev => ({ ...prev, peso: parseFloat(e.target.value) || 0 }))}
      onBlur={onBlur}
      leftIcon={<Weight className="w-3.5 h-3.5" />}
      rightIcon={showCheck ? renderCheck?.() : <span className="text-xs font-medium">KG</span>}
      error={errors.peso}
      size="small"
      placeholder="0.000"
    />
  );
};


